import React from 'react';

const TermsAndConditions = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-primary to-primary-dark text-white py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-extrabold mb-4">Terms and Conditions</h1>
          <p className="text-xl text-white/90">
            Last Updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12 space-y-8">
          <section>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">1. Acceptance of Terms</h2>
            <p className="text-gray-700 leading-relaxed">
              By accessing and using ACT PAWS ("the Service"), you accept and agree to be bound by the 
              terms and provision of this agreement. If you do not agree to abide by the above, please 
              do not use this service.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">2. Description of Service</h2>
            <p className="text-gray-700 leading-relaxed mb-3">
              ACT PAWS is a pet activity tracking platform that allows users to:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Add and manage multiple pet profiles</li>
              <li>Track pet health, fitness, and daily activities</li>
              <li>Monitor daily, weekly, and monthly routines</li>
              <li>Receive health progress insights and analytics</li>
              <li>Access pet-related services and resources</li>
            </ul>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">3. User Accounts</h2>
            <h3 className="text-2xl font-semibold text-primary mb-3 mt-6">3.1 Account Creation</h3>
            <p className="text-gray-700 leading-relaxed mb-3">
              To use certain features of the Service, you must register for an account. You agree to:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Provide accurate, current, and complete information during registration</li>
              <li>Maintain and promptly update your account information</li>
              <li>Maintain the security of your password and identification</li>
              <li>Accept all responsibility for all activities that occur under your account</li>
              <li>Notify us immediately of any unauthorized use of your account</li>
            </ul>

            <h3 className="text-2xl font-semibold text-primary mb-3 mt-6">3.2 Account Responsibility</h3>
            <p className="text-gray-700 leading-relaxed">
              You are responsible for maintaining the confidentiality of your account credentials and 
              for all activities that occur under your account. ACT PAWS is not liable for any loss 
              or damage arising from your failure to comply with this security obligation.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">4. User Conduct</h2>
            <p className="text-gray-700 leading-relaxed mb-3">You agree not to:</p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Use the Service for any illegal purpose or in violation of any laws</li>
              <li>Upload, post, or transmit any content that is harmful, offensive, or violates the rights of others</li>
              <li>Impersonate any person or entity or falsely state or misrepresent your affiliation</li>
              <li>Interfere with or disrupt the Service or servers connected to the Service</li>
              <li>Attempt to gain unauthorized access to any portion of the Service</li>
              <li>Use automated systems (bots, scrapers) to access the Service without permission</li>
              <li>Reverse engineer, decompile, or disassemble any part of the Service</li>
            </ul>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">5. Pet Information and Data</h2>
            <h3 className="text-2xl font-semibold text-primary mb-3 mt-6">5.1 Accuracy of Information</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              You are responsible for the accuracy of all pet information and data you enter into the 
              Service. ACT PAWS is not responsible for any errors or omissions in the information 
              you provide.
            </p>

            <h3 className="text-2xl font-semibold text-primary mb-3">5.2 Data Ownership</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              You retain ownership of all data you upload to the Service. By using the Service, 
              you grant ACT PAWS a license to use, store, and process your data solely for the 
              purpose of providing and improving the Service.
            </p>

            <h3 className="text-2xl font-semibold text-primary mb-3">5.3 Data Backup</h3>
            <p className="text-gray-700 leading-relaxed">
              While we make reasonable efforts to protect your data, you are responsible for 
              maintaining backups of important information. ACT PAWS is not liable for any loss 
              of data.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">6. Health and Medical Information</h2>
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-r-lg mb-4">
              <p className="text-gray-800 font-semibold mb-2">Important:</p>
              <p className="text-gray-700 leading-relaxed">
                ACT PAWS is a tracking and monitoring tool and is not intended to replace professional 
                veterinary care. The Service provides information and insights for informational purposes 
                only and should not be used as a substitute for professional medical advice, diagnosis, 
                or treatment.
              </p>
            </div>
            <p className="text-gray-700 leading-relaxed">
              Always seek the advice of a qualified veterinarian with any questions regarding your 
              pet's health. Never disregard professional veterinary advice or delay seeking it 
              because of something you have read or seen on the Service.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">7. Subscription and Payment</h2>
            <h3 className="text-2xl font-semibold text-primary mb-3 mt-6">7.1 Free and Paid Services</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              ACT PAWS may offer both free and paid subscription plans. Features and functionality 
              may vary between plans. We reserve the right to modify, suspend, or discontinue 
              any plan at any time.
            </p>

            <h3 className="text-2xl font-semibold text-primary mb-3">7.2 Payment Terms</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              If you purchase a subscription, you agree to pay all fees associated with your 
              selected plan. Payments are processed through third-party payment processors. 
              All fees are non-refundable unless otherwise stated.
            </p>

            <h3 className="text-2xl font-semibold text-primary mb-3">7.3 Subscription Renewal</h3>
            <p className="text-gray-700 leading-relaxed">
              Subscriptions automatically renew unless cancelled before the renewal date. You 
              may cancel your subscription at any time through your account settings.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">8. Intellectual Property</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              The Service and its original content, features, and functionality are owned by ACT PAWS 
              and are protected by international copyright, trademark, patent, trade secret, and other 
              intellectual property laws.
            </p>
            <p className="text-gray-700 leading-relaxed">
              You may not copy, modify, distribute, sell, or lease any part of the Service without 
              our prior written consent.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">9. Limitation of Liability</h2>
            <p className="text-gray-700 leading-relaxed">
              To the maximum extent permitted by law, ACT PAWS shall not be liable for any indirect, 
              incidental, special, consequential, or punitive damages, or any loss of profits or 
              revenues, whether incurred directly or indirectly, or any loss of data, use, goodwill, 
              or other intangible losses resulting from your use of the Service.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">10. Indemnification</h2>
            <p className="text-gray-700 leading-relaxed">
              You agree to indemnify and hold harmless ACT PAWS, its officers, directors, employees, 
              and agents from any claims, damages, losses, liabilities, and expenses (including legal 
              fees) arising out of or relating to your use of the Service, violation of these Terms, 
              or infringement of any rights of another.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">11. Termination</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We may terminate or suspend your account and access to the Service immediately, without 
              prior notice or liability, for any reason, including if you breach these Terms. Upon 
              termination, your right to use the Service will immediately cease.
            </p>
            <p className="text-gray-700 leading-relaxed">
              You may terminate your account at any time by following the account deletion process 
              outlined in our Delete Account page.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">12. Changes to Terms</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We reserve the right to modify or replace these Terms at any time. If a revision is 
              material, we will provide at least 30 days' notice prior to any new terms taking effect. 
              What constitutes a material change will be determined at our sole discretion.
            </p>
            <p className="text-gray-700 leading-relaxed">
              By continuing to access or use the Service after any revisions become effective, you 
              agree to be bound by the revised terms.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">13. Governing Law</h2>
            <p className="text-gray-700 leading-relaxed">
              These Terms shall be governed by and construed in accordance with the laws of the 
              jurisdiction in which ACT PAWS operates, without regard to its conflict of law provisions.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">14. Contact Information</h2>
            <p className="text-gray-700 leading-relaxed mb-3">If you have any questions about these Terms and Conditions, please contact us:</p>
            <ul className="space-y-2 text-gray-700">
              <li><strong>Email:</strong> <a href="mailto:support@actpaws.com" className="text-primary hover:underline">support@actpaws.com</a></li>
              <li><strong>Phone:</strong> <a href="tel:+919980363932" className="text-primary hover:underline">+91 9980363932</a></li>
            </ul>
          </section>
        </div>
      </div>
    </div>
  );
};

export default TermsAndConditions;
